import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { ErrorBoundary } from './lib/ErrorBoundary';
import { supabase } from './lib/supabase';

/**
 * The crash fallback's "Sign out". supabase-js signOut() does NOT clear the
 * local session when the server call fails (it returns { error } and stops
 * before _removeSession) — so on shop wifi that is exactly the case where
 * remounting would land straight back in the same crash. Fall back to
 * removing the client's own persisted session keys (all prefixed 'sb-') so the
 * remount genuinely starts signed out. Gemini's catch in the 3-party review.
 */
async function signOutHard(): Promise<void> {
  let failed = false;
  try {
    const { error } = await supabase.auth.signOut();
    failed = error !== null;
  } catch {
    failed = true;
  }
  if (failed) {
    for (const key of Object.keys(window.localStorage)) {
      if (key.startsWith('sb-')) window.localStorage.removeItem(key);
    }
  }
}

const root = document.getElementById('root');
if (!root) throw new Error('No #root element in index.html');

createRoot(root).render(
  <StrictMode>
    {/* Pre-P8 punch list, Tier 2: the GLOBAL error boundary. Without one,
        React unmounts the whole tree on any uncaught render error — a blank
        page with no message. Dashboard.tsx mounts a second, per-view
        boundary inside this one so a crash in a single view keeps the
        portal's header and navigation alive; this outer one is the last
        line of defence for everything else (login, the request form, the
        shell itself). onSignOut is the one exit that cannot route straight
        back into a crash that is deterministic for a signed-in manager (App
        sends every persisted session to the Dashboard). See
        lib/ErrorBoundary.tsx. */}
    <ErrorBoundary scope="portal" onSignOut={signOutHard}>
      <App />
    </ErrorBoundary>
  </StrictMode>,
);
